// contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from "react";
import { baseAxios } from "../apis";
import { useCsrfToken } from "../hooks/useCsrfToken";
import { useToast } from "./toastContext";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { addToast } = useToast();
  const { fetchCsrfToken } = useCsrfToken();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [error, setError] = useState("");

  const fetchUser = async (isUseLoading = true) => {
    if (isUseLoading) {
      setIsLoading(true);
    }
    try {
      const freshToken = await fetchCsrfToken();
      const { data } = await baseAxios.get("/api/user-detail");
      const fetchedUser = data.data;
      setUser(fetchedUser);
      if (!localStorage.getItem("isAuthenticated")) {
        localStorage.setItem("isAuthenticated", "true");
      }

      if (fetchedUser?.is_tourtip) {
        localStorage.removeItem("tour_disabled_botlist");
        localStorage.removeItem("tour_disabled_dashboard");
      }

      if (fetchedUser && !fetchedUser.referral_code) {
        try {
          await baseAxios.post(
            `/api/generate-referral`,
            {},
            {
              withCredentials: true,
              headers: { "X-CSRFToken": freshToken },
            }
          );
          fetchUser();
        } catch (referralError) {
          console.error("Failed to generate referral code:", referralError);
        }
      }

      const stripeCustomerId = fetchedUser?.stripe_customer_id;
      if (!stripeCustomerId) {
        setIsLoading(false);
        return;
      }

      fetchSubscription(stripeCustomerId);
    } catch (err) {
      //  Set user to null (not false) to indicate no authenticated user
      setUser(null);
      console.error("Failed to fetch user:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubscription = async (stripeCustomerId) => {
    try {
      setLoadingSubscription(true);
      const subRes = await baseAxios.get(
        `/api/get-subscription/${stripeCustomerId}`
      );
      setSubscription(subRes.data);
    } catch (err) {
    } finally {
      setLoadingSubscription(false);
    }
  };

  //  Forgot password (request reset)
  const requestPasswordReset = async (email) => {
    setError("");
    try {
      const freshToken = await fetchCsrfToken();
      await baseAxios.post(
        "/api/forgot-password/",
        { email },
        {
          withCredentials: true,
          headers: { "X-CSRFToken": freshToken },
        }
      );
      return true;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        "Failed to request password reset. Please try again.";
      setError(errorMessage);
      throw errorMessage;
    }
  };

  //  Confirm reset password
  const confirmPasswordReset = async ({ uid, token, newPassword }) => {
    setError("");
    try {
      const freshToken = await fetchCsrfToken();
      await baseAxios.post(
        "/api/reset-password/",
        {
          uid,
          token,
          new_password: newPassword,
        },
        {
          withCredentials: true,
          headers: { "X-CSRFToken": freshToken },
        }
      );
      return true;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        "Failed to reset password. Please try again.";
      setError(errorMessage);
      throw errorMessage;
    }
  };

  const logout = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    try {
      setIsLoading(true);
      const token = await fetchCsrfToken();

      await baseAxios.post(
        `/api/logout/`,
        {},
        {
          withCredentials: true,
          headers: { "X-CSRFToken": token },
        }
      );

      setUser(null);

      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      // Provide user feedback on failure
      addToast("error", "Logout failed. Please try again.", 7500);

      // For safety, still attempt to clear local data even if server call fails
      localStorage.clear();
      sessionStorage.clear();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        fetchUser,
        isLoading,
        subscription,
        loadingSubscription,
        fetchSubscription,
        requestPasswordReset,
        confirmPasswordReset,
        error,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
